import { cConst, cRange, defaultLayer, gradient, vConst, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Reference effect #3: ground-pound shockwave (plan §"Reference effects").
 * Procedural expanding ring mesh + dust flipbook + chunky debris — the layer
 * type pure particle libraries can't do (§3.3).
 */
export const groundPoundEffect = {
    version: 1,
    meta: { name: 'Ground Pound', duration: 1.1, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'gp_flash',
            name: 'impact flash',
            delay: 0,
            duration: 0.05,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.15),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.8),
                    rotation: vConst(0),
                    color: cConst([1, 0.95, 0.8, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.8, 0.4, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'gp_ring',
            name: 'shockwave ring mesh',
            delay: 0.02,
            duration: 0.05,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.55),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.6), // final radius; size curve expands toward it
                    rotation: vConst(0),
                    color: cConst([1, 0.92, 0.7, 0.95]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 72,
                    // rim starts fat and thins as the wave travels
                    width: {
                        preset: 'custom',
                        points: [
                            { t: 0, v: 1, outTan: 0 },
                            { t: 1, v: 0.15, inTan: -0.8 },
                        ],
                    },
                    widthScale: 0.55,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.55, [1, 0.85, 0.5, 0.8]], [1, [0.9, 0.6, 0.3, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'gp_dust',
            name: 'dust flipbook',
            delay: 0.05,
            duration: 0.08,
            maxParticles: 24,
            emitter: {
                mode: { type: 'burst', count: 11 },
                shape: { type: 'ring', radius: 0.7 },
                initial: {
                    lifetime: vRange(0.6, 0.95),
                    speed: vRange(1.4, 2.4),
                    spread: vConst(14),
                    size: vRange(0.8, 1.3),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-40, 40),
                    color: cRange([0.78, 0.7, 0.6, 0.85], [0.62, 0.55, 0.48, 0.85]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'drag', enabled: true, coefficient: 2.5 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.15, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'gp_debris',
            name: 'debris chunks',
            delay: 0.02,
            duration: 0.06,
            maxParticles: 16,
            emitter: {
                mode: { type: 'burst', count: 9 },
                shape: { type: 'cone', angle: 42, radius: 0.35 },
                initial: {
                    lifetime: vRange(0.5, 0.85),
                    speed: vRange(3.5, 6.5),
                    spread: vConst(10),
                    size: vRange(0.09, 0.2),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-900, 900),
                    color: cRange([0.65, 0.5, 0.38, 1], [0.45, 0.36, 0.3, 1]),
                },
            },
            renderer: { type: 'mesh', mesh: { type: 'builtin', id: 'shard' } },
            modules: [
                { type: 'gravity', enabled: true, strength: 22 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
            ],
            material: { blend: 'alpha', sprite: { type: 'procedural', hardness: 0.5 } },
        }),
    ],
};
